<script setup lang="ts">
import type { EventResponse } from '@@/types/api';
import type { ViewerRole } from '~/utils/event-policy';
import { useOrganizerActions } from '~/composables/use-organizer-actions';

const props = defineProps<{
  event: EventResponse;
  viewerRole: ViewerRole;
  refreshEvent: () => Promise<void>;
}>();

const eventRef = computed(() => props.event as EventResponse | null);
const viewerRoleRef = computed(() => props.viewerRole);

const {
  primaryTransition,
  secondaryTransitions,
  canEdit,
  needsStripe,
  isStripeOnboarded,
  paymentMethods,
  isLoading,
  isTransitioning,
  transitionError,
  showSubmitReviewDialog,
  showConfirmDialog,
  showSubmitSuccess,
  pendingTransition,
  initializeStripeContext,
  openPrimaryAction,
  openAction,
  submitForReview,
  confirmTransition,
  handleCardAdded,
  clearError,
} = useOrganizerActions({
  event: eventRef,
  viewerRole: viewerRoleRef,
  refreshEvent: props.refreshEvent,
});

onMounted(() => {
  initializeStripeContext();
});
</script>

<template>
  <div class="organizer-actions-panel">
    <!-- Stripe warning (hidden while loading to prevent flash) -->
    <v-alert
      v-if="needsStripe && !isLoading"
      type="warning"
      variant="tonal"
      class="mb-4"
      prominent
    >
      <div class="d-flex align-center">
        <div>
          <div class="font-weight-bold">Stripe Account Required</div>
          <div class="text-body-2">
            You need to complete Stripe onboarding before you can start
            fundraising.
          </div>
        </div>
        <v-spacer />
        <v-btn
          variant="flat"
          color="warning"
          size="small"
          to="/dashboard/stripe"
        >
          Complete Setup
        </v-btn>
      </div>
    </v-alert>

    <!-- Transition error -->
    <v-alert
      v-if="transitionError"
      type="error"
      variant="tonal"
      class="mb-4"
      closable
      @click:close="clearError"
    >
      {{ transitionError.message }}
      <ul v-if="transitionError.fields" class="mt-2 ml-4">
        <li
          v-for="msg in [
            ...new Set(Object.values(transitionError.fields).flat()),
          ]"
          :key="msg"
        >
          {{ msg }}
        </li>
      </ul>
    </v-alert>

    <!-- Loading skeleton -->
    <v-skeleton-loader v-if="isLoading" type="button" class="mb-2" />

    <template v-else>
      <!-- Primary transition button -->
      <v-btn
        v-if="primaryTransition"
        :color="primaryTransition.variant"
        :prepend-icon="primaryTransition.icon"
        :loading="isTransitioning"
        :disabled="
          primaryTransition.action === 'start_fundraising' && !isStripeOnboarded
        "
        variant="flat"
        block
        class="mb-2"
        @click="openPrimaryAction"
      >
        {{ primaryTransition.label }}
      </v-btn>

      <!-- Secondary transitions (e.g. cancel for approved events) -->
      <v-btn
        v-for="transition in secondaryTransitions"
        :key="transition.action"
        :color="transition.variant"
        :prepend-icon="transition.icon"
        :loading="isTransitioning"
        variant="outlined"
        block
        class="mb-2"
        @click="openAction(transition)"
      >
        {{ transition.label }}
      </v-btn>

      <!-- Edit Event button -->
      <v-btn
        v-if="canEdit"
        variant="outlined"
        :to="`/events/${event.id}/edit`"
        prepend-icon="mdi-pencil"
        block
      >
        Edit Event
      </v-btn>
    </template>

    <!-- Dialogs -->
    <EventsTransitionConfirmDialog
      v-model="showConfirmDialog"
      :transition="pendingTransition"
      :is-loading="isTransitioning"
      @confirm="confirmTransition"
    />

    <ClientOnly>
      <EventsSubmitForReviewDialog
        v-model="showSubmitReviewDialog"
        :payment-methods="paymentMethods"
        :is-submitting="isTransitioning"
        @submit="submitForReview"
        @card-added="handleCardAdded"
      />
    </ClientOnly>

    <EventsSubmitForReviewSuccessDialog v-model="showSubmitSuccess" />
  </div>
</template>
